import type { Session } from '@supabase/supabase-js';
import {
  buildAuthScopeKey,
  parseCompatibleJwtAuthorizationClaims,
  parseJwtPayload,
  parseJwtAuthorizationClaims,
} from '@task-tracker/shared';
import { queryClient } from './query-client';
import { purgePersistedCache } from './query-persist';
import { supabase } from './supabase';
import { useAuthStore } from '../stores/auth.store';

const SESSION_REFRESH_LEEWAY_MS = 60_000;

export type SessionRecoveryResult =
  | {
      status: 'fresh';
      session: Session;
      rotated: boolean;
    }
  | {
      status: 'signed_out';
      session: null;
      rotated: false;
    }
  | {
      status: 'miss';
      session: null;
      rotated: false;
    };

let refreshPromise: Promise<SessionRecoveryResult> | null = null;

function missSessionRecovery(): SessionRecoveryResult {
  return {
    status: 'miss',
    session: null,
    rotated: false,
  };
}

function signedOutSessionRecovery(): SessionRecoveryResult {
  return {
    status: 'signed_out',
    session: null,
    rotated: false,
  };
}

export function getSessionScope(session: Session | null): string {
  return buildAuthScopeKey({
    token: session?.access_token ?? null,
    userId: session?.user?.id ?? null,
  });
}

export function hasCurrentSessionClaims(session: Session | null): boolean {
  if (!session?.access_token) return false;
  return parseJwtAuthorizationClaims(session.access_token) !== null;
}

function getSessionExpiryMs(session: Session | null): number | null {
  if (!session?.access_token) return null;
  const payload = parseJwtPayload(session.access_token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
}

export function isSessionExpiringSoon(
  session: Session | null,
  leewayMs = SESSION_REFRESH_LEEWAY_MS,
): boolean {
  const expiryMs = getSessionExpiryMs(session);
  if (!expiryMs) return true;
  return expiryMs <= Date.now() + leewayMs;
}

function hasCompatibleSessionClaims(session: Session | null): boolean {
  if (!session?.access_token) return false;
  return parseCompatibleJwtAuthorizationClaims(session.access_token) !== null;
}

function isSessionReadyForApi(session: Session | null): boolean {
  return hasCurrentSessionClaims(session) && !isSessionExpiringSoon(session);
}

function isCompatibleFreshSession(session: Session | null): boolean {
  return hasCompatibleSessionClaims(session) && !isSessionExpiringSoon(session);
}

function isHardRefreshFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const { name, status } = error as {
    name?: unknown;
    status?: unknown;
  };

  if (name === 'AuthSessionMissingError') return true;
  return status === 400 || status === 401 || status === 403;
}

export function syncSessionToStore(session: Session | null, previousScope?: string): string {
  // Whether a session was already known BEFORE this call. On a fresh page load
  // the store always starts empty, so `prevScope` is 'anonymous' purely because
  // nothing has been read yet — not because the user changed.
  const hadKnownSession = useAuthStore.getState().session !== null;
  const prevScope = previousScope ?? getSessionScope(useAuthStore.getState().session);
  const nextScope = getSessionScope(session);

  // Clear only on a genuine identity change WITHIN a session — a different user,
  // a role change, or a sign-out. `hadKnownSession` is what distinguishes that
  // from ordinary boot hydration.
  //
  // Without that guard this ran on every single page load: the persisted cache
  // was restored, then immediately destroyed by this clear before any component
  // could read it. With the API reachable the queries just refetched and
  // repopulated the cache, so it *looked* like persistence worked — it never
  // served a single cached read. Only killing the API made the emptiness
  // visible.
  //
  // Dropping the clear here is safe because it was never the real defence: the
  // persister refuses to hydrate any cache whose stamped auth scope differs
  // from the live one (see lib/query-persist.ts), and at boot the in-memory
  // cache is empty regardless.
  if (hadKnownSession && prevScope !== nextScope) {
    queryClient.clear();
    // The in-memory cache is gone; the on-disk copy must go with it. The
    // persisted buster embeds the scope, so a stale blob could not be hydrated
    // by the next user anyway — but leaving one account's data sitting in
    // localStorage after a sign-out or role change is not something to rely on
    // a version check to excuse.
    purgePersistedCache();
  }

  const { setAuth, clearAuth } = useAuthStore.getState();
  if (session) {
    setAuth(session.user, session);
  } else {
    clearAuth();
  }

  return nextScope;
}

function finishWithFreshSession(
  session: Session,
  previousScope: string,
  previousSession: Session | null,
): SessionRecoveryResult {
  const nextScope = syncSessionToStore(session, previousScope);
  const rotated = previousSession?.access_token !== session.access_token;

  if (rotated && nextScope === previousScope) {
    // The scope key covers user id, role, role_version, project_visibility and
    // admin status, so an unchanged scope means *nothing the user can see has
    // changed* — only the bearer credential was swapped. Cached data is still
    // valid and must stay fresh.
    //
    // This used to be an unfiltered invalidateQueries(), which marked every
    // query in the app stale. reconcileSession runs on both `focus` and
    // `visibilitychange`, and a session within SESSION_REFRESH_LEEWAY_MS (60 s)
    // of expiry rotates on each one — so returning to the tab could refetch the
    // entire screen despite valid cached data, defeating the SWR behaviour.
    //
    // Errored queries are the exception worth keeping: a query that failed
    // while the old token was expiring should get another chance now that a
    // good credential exists. Successful data is left alone.
    void queryClient.invalidateQueries({
      predicate: (query) => query.state.status === 'error',
    });
  }

  return {
    status: 'fresh',
    session,
    rotated,
  };
}

export async function refreshSessionOrClear(
  fallbackSession: Session | null = useAuthStore.getState().session,
): Promise<SessionRecoveryResult> {
  if (refreshPromise) return refreshPromise;

  const previousScope = getSessionScope(useAuthStore.getState().session);
  const inFlight = (async (): Promise<SessionRecoveryResult> => {
    try {
      const { data, error } = await supabase.auth.refreshSession(fallbackSession ?? undefined);
      const session = !error ? data.session : null;

      if (session && isCompatibleFreshSession(session)) {
        return finishWithFreshSession(session, previousScope, fallbackSession);
      }

      if (error && !isHardRefreshFailure(error)) {
        return missSessionRecovery();
      }

      if (session && hasCompatibleSessionClaims(session)) {
        return missSessionRecovery();
      }

      syncSessionToStore(null, previousScope);
      return signedOutSessionRecovery();
    } catch (error) {
      if (!isHardRefreshFailure(error)) {
        return missSessionRecovery();
      }

      syncSessionToStore(null, previousScope);
      return signedOutSessionRecovery();
    }
  })();

  refreshPromise = inFlight.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise!;
}

export async function reconcileSession(session: Session | null): Promise<SessionRecoveryResult> {
  const previousScope = getSessionScope(useAuthStore.getState().session);

  if (!session) {
    syncSessionToStore(null, previousScope);
    return signedOutSessionRecovery();
  }

  if (isSessionReadyForApi(session)) {
    syncSessionToStore(session, previousScope);
    return {
      status: 'fresh',
      session,
      rotated: false,
    };
  }

  return refreshSessionOrClear(session);
}
